import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { getPaginationState, JhiItemCount, JhiPagination, TextFormat } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DESC, ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { deleteEntity, getEntities } from './order.reducer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { APP_TIMESTAMP_FORMAT, AUTHORITIES } from 'app/config/constants';
import { colors, formatVND } from 'app/shared/theme/colors';
import { toast } from 'react-toastify';
import { PageSizeSelector } from 'app/shared/components/page-size-selector';
import axios from 'axios';
import { useTranslation } from 'app/shared/i18n';
const TIME_FILTER_KEYS = [
  { key: 'order.allTime', hours: 0 },
  { key: 'order.12h', hours: 12 },
  { key: 'order.24h', hours: 24 },
  { key: 'order.3d', hours: 72 },
  { key: 'order.7d', hours: 168 },
  { key: 'order.30d', hours: 720 },
];

export const Order = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const pageLocation = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'createdDate', 'DESC'), pageLocation.search),
  );
  const [viewOrder, setViewOrder] = useState(null);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [searchCode, setSearchCode] = useState('');
  const [timeFilter, setTimeFilter] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTimer, setSearchTimer] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);

  const orderList = useAppSelector(state => state.order.entities);
  const totalItems = useAppSelector(state => state.order.totalItems);
  const loading = useAppSelector(state => state.order.loading);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const hasDateRange = fromDate && toDate;
  const hasAnyFilter = searchCode || timeFilter > 0 || hasDateRange;

  const buildQuery = (search?: string, hours?: number, fd?: string, td?: string) => {
    const s = search ?? searchCode;
    const h = hours ?? timeFilter;
    const f = fd ?? fromDate;
    const td2 = td ?? toDate;
    const parts: string[] = [];
    if (s) parts.push(`search:${s}`);
    if (f && td2) {
      parts.push(`fromDate:${f}`);
      parts.push(`toDate:${td2}`);
    } else if (h > 0) {
      parts.push(`hours:${h}`);
    }
    return parts.length > 0 ? parts.join('|') : undefined;
  };

  const doFetch = (page: number, search?: string, hours?: number, fd?: string, td?: string) => {
    dispatch(
      getEntities({
        page,
        size: pageSize,
        sort: `${pagination.sort},${pagination.order}`,
        query: buildQuery(search, hours, fd, td),
      }),
    );
  };

  const sortEntities = () => {
    doFetch(pagination.activePage - 1);
    const endURL = `?page=${pagination.activePage}&sort=${pagination.sort},${pagination.order}`;
    if (pageLocation.search !== endURL) navigate(`${pageLocation.pathname}${endURL}`);
  };

  useEffect(() => {
    sortEntities();
  }, [pagination.activePage, pagination.order, pagination.sort, pageSize, timeFilter]);

  useEffect(() => {
    axios
      .get('/api/account/order-retention-config')
      .then(res => setRetentionDays(res.data?.current ?? null))
      .catch(() => {
        /* ignore */
      });
  }, []);

  const handlePagination = cp => setPagination({ ...pagination, activePage: cp });
  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setPagination({ ...pagination, activePage: 1 });
  };

  const handleSearch = (value: string) => {
    setSearchCode(value);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(
      setTimeout(() => {
        setPagination(prev => ({ ...prev, activePage: 1 }));
        doFetch(0, value);
      }, 400),
    );
  };

  const handleTimeFilter = (hours: number) => {
    setTimeFilter(hours);
    setFromDate('');
    setToDate('');
    setPagination(prev => ({ ...prev, activePage: 1 }));
  };

  const handleDateRangeApply = () => {
    if (!fromDate || !toDate) {
      toast.warning(t('order.selectDateRange'));
      return;
    }
    if (fromDate > toDate) {
      toast.warning(t('order.startBeforeEnd'));
      return;
    }
    setTimeFilter(0); // clear pill filter
    setPagination(prev => ({ ...prev, activePage: 1 }));
    doFetch(0, undefined, 0, fromDate, toDate);
  };

  const clearDateRange = () => {
    setFromDate('');
    setToDate('');
    setPagination(prev => ({ ...prev, activePage: 1 }));
    doFetch(0, undefined, 0, '', '');
  };

  const onDownload = (result: string, code: string) => {
    const data = JSON.parse(result).join('\n');
    const blob = new Blob([data], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${code}.txt`;
    link.click();
  };

  const convertMailPass = (x: string) => {
    const parts = x.split('|');
    return parts[0] + '|' + parts[1];
  };
  const onCopy = (result: string) => {
    navigator.clipboard.writeText(
      JSON.parse(result)
        .map(x => convertMailPass(x))
        .join('\n'),
    );
    toast.success(t('order.copySuccess'));
  };
  const onCopyFullInfo = (result: string) => {
    navigator.clipboard.writeText(JSON.parse(result).join('\n'));
    toast.success(t('order.copySuccess'));
  };

  // Select
  const toggleSelect = (id: number) => setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleSelectAll = () => {
    const ids = (orderList || []).map(o => o.id).filter(Boolean) as number[];
    setSelectedIds(prev => (prev.length === ids.length ? [] : ids));
  };

  const downloadFromList = (orders: any[]) => {
    const data = orders
      .filter(o => o.result)
      .map(o => JSON.parse(o.result).join('\n'))
      .join('\n');
    if (!data) {
      toast.info(t('order.noDataToDownload'));
      return;
    }
    const blob = new Blob([data], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
  };

  const onDownloadAll = async () => {
    setDownloading(true);
    try {
      let url = 'api/orders/download-all?';
      if (searchCode) url += `search=${encodeURIComponent(searchCode)}&`;
      if (fromDate && toDate) {
        url += `fromDate=${fromDate}&toDate=${toDate}&`;
      } else if (timeFilter > 0) {
        url += `hours=${timeFilter}&`;
      }
      const { data } = await axios.get(url);
      const orders = (data || []).filter((o: any) => o.result);
      if (orders.length === 0) {
        toast.info(t('order.noDataToDownload'));
        return;
      }
      const content = orders.map((o: any) => JSON.parse(o.result).join('\n')).join('\n');
      const blob = new Blob([content], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `orders_all_${new Date().toISOString().slice(0, 10)}.txt`;
      link.click();
      toast.success(`${t('order.downloadSuccess')} ${orders.length} ${t('order.downloadedOrders')}`);
    } catch {
      toast.error(t('order.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  const onDeleteSelected = async () => {
    if (!window.confirm(`${t('order.deleteConfirm')} ${selectedIds.length} ${t('order.deleteConfirmSuffix')}`)) return;
    for (const id of selectedIds) {
      await dispatch(deleteEntity(id));
    }
    setSelectedIds([]);
    toast.success(t('order.deleted'));
  };

  const inputBase: React.CSSProperties = {
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 12,
    paddingRight: 12,
    borderRadius: 8,
    fontSize: 13,
    outline: 'none',
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.text,
    transition: 'border-color .15s',
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '36px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{isAdmin ? t('order.adminTitle') : t('order.userTitle')}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="ms-btn ms-btn--secondary"
            onClick={sortEntities}
            disabled={loading}
            style={{ padding: '7px 14px', fontSize: 13 }}
          >
            <FontAwesomeIcon icon="sync" spin={loading} style={{ marginRight: 6 }} /> {t('order.refresh')}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ms-card" style={{ padding: '16px 18px', marginBottom: 16 }}>
        {/* Row 1: Search + Download All */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <input
              type="text"
              value={searchCode}
              onChange={e => handleSearch(e.target.value)}
              placeholder={t(isAdmin ? 'order.searchPlaceholderAdmin' : 'order.searchPlaceholder')}
              style={{ ...inputBase, width: '100%', paddingLeft: 38 }}
            />
            <span
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.dim,
                fontSize: 13,
                pointerEvents: 'none',
              }}
            >
              <FontAwesomeIcon icon="search" />
            </span>
          </div>
          {searchCode && (
            <button className="ms-btn ms-btn--ghost" onClick={() => handleSearch('')} style={{ padding: '6px 10px', fontSize: 11 }}>
              {t('order.clear')}
            </button>
          )}

          {/* Download All Button - prominent */}
          <button
            onClick={onDownloadAll}
            disabled={downloading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 20px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              cursor: downloading ? 'wait' : 'pointer',
              background: `linear-gradient(135deg, ${colors.accent}, #6366f1)`,
              color: '#fff',
              border: 'none',
              letterSpacing: '.02em',
              boxShadow: `0 4px 14px ${colors.accent}55`,
              opacity: downloading ? 0.7 : 1,
              transition: 'all .2s',
            }}
          >
            <FontAwesomeIcon icon={downloading ? 'spinner' : 'download'} spin={downloading} />
            <span>{downloading ? t('order.downloading') : hasAnyFilter ? t('order.downloadFiltered') : t('order.downloadAll')}</span>
            {hasAnyFilter && !downloading && (
              <span
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  padding: '1px 8px',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                <FontAwesomeIcon icon="filter" style={{ marginRight: 3 }} />
                Filtered
              </span>
            )}
          </button>
        </div>

        {/* Row 2: Quick time pills */}
        <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: colors.dim, marginRight: 4, fontWeight: 600 }}>{t('order.quickFilter')}:</span>
          {TIME_FILTER_KEYS.map(tf => (
            <button
              key={tf.hours}
              onClick={() => handleTimeFilter(tf.hours)}
              style={{
                padding: '4px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                background: timeFilter === tf.hours && !hasDateRange ? colors.accent : colors.surface,
                color: timeFilter === tf.hours && !hasDateRange ? '#fff' : colors.muted,
                border: `1px solid ${timeFilter === tf.hours && !hasDateRange ? colors.accent : colors.border}`,
                transition: 'all .15s',
              }}
            >
              {t(tf.key)}
            </button>
          ))}
        </div>

        {/* Retention info */}
        {retentionDays !== null && (
          <div
            style={{
              display: 'inline-flex',
              gap: 10,
              marginTop: 12,
              alignItems: 'center',
              padding: '8px 16px',
              borderRadius: 10,
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <span style={{ fontSize: 13, color: colors.text }}>
              🗑️ {t('orderRetention.autoDeleteInfo')}:{' '}
              <strong style={{ color: '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                {retentionDays} {t('orderRetention.days')}
              </strong>
            </span>
            <Link
              to="/profile"
              style={{
                fontSize: 12,
                color: colors.accent,
                textDecoration: 'none',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 6,
                background: `${colors.accent}15`,
                border: `1px solid ${colors.accent}33`,
              }}
            >
              {t('orderRetention.changeInProfile')} →
            </Link>
          </div>
        )}

        {/* Row 3: Date range picker */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginTop: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '10px 14px',
            borderRadius: 10,
            background: hasDateRange ? `${colors.accent}08` : colors.surface,
            border: `1px solid ${hasDateRange ? colors.accent + '33' : colors.border}`,
          }}
        >
          <span style={{ fontSize: 11, color: colors.dim, fontWeight: 600, whiteSpace: 'nowrap' }}>
            <FontAwesomeIcon icon="calendar-alt" style={{ marginRight: 4 }} />
            {t('order.dateRange')}:
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 280 }}>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{
                ...inputBase,
                flex: 1,
                minWidth: 130,
                borderColor: hasDateRange ? colors.accent + '66' : colors.border,
                colorScheme: 'dark',
              }}
            />
            <span style={{ color: colors.dim, fontSize: 12, fontWeight: 600 }}>—</span>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{
                ...inputBase,
                flex: 1,
                minWidth: 130,
                borderColor: hasDateRange ? colors.accent + '66' : colors.border,
                colorScheme: 'dark',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleDateRangeApply}
              disabled={!fromDate || !toDate}
              style={{
                padding: '6px 16px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                background: fromDate && toDate ? colors.accent : colors.surface,
                color: fromDate && toDate ? '#fff' : colors.dim,
                border: `1px solid ${fromDate && toDate ? colors.accent : colors.border}`,
                opacity: !fromDate || !toDate ? 0.5 : 1,
                transition: 'all .15s',
              }}
            >
              <FontAwesomeIcon icon="check" style={{ marginRight: 4 }} />
              {t('order.apply')}
            </button>
            {hasDateRange && (
              <button
                onClick={clearDateRange}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  cursor: 'pointer',
                  background: 'transparent',
                  color: colors.danger,
                  border: `1px solid ${colors.danger}44`,
                }}
              >
                <FontAwesomeIcon icon="times" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 12,
            padding: '10px 16px',
            borderRadius: 8,
            background: colors.accentDim,
            border: `1px solid ${colors.accent}33`,
          }}
        >
          <span style={{ fontSize: 13, color: colors.accent, fontWeight: 600 }}>
            {t('order.selected')} {selectedIds.length} {t('order.selectedOrders')}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="ms-btn ms-btn--primary"
              onClick={() => downloadFromList((orderList || []).filter(o => selectedIds.includes(o.id)))}
              style={{ padding: '5px 14px', fontSize: 12 }}
            >
              {t('order.downloadSelected')}
            </button>
            <button className="ms-btn ms-btn--danger" onClick={onDeleteSelected} style={{ padding: '5px 14px', fontSize: 12 }}>
              {t('order.deleteSelected')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="ms-card" style={{ overflow: 'hidden' }}>
        {orderList && orderList.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="ms-table" style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.length === orderList.length && orderList.length > 0}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer', accentColor: colors.accent }}
                      />
                    </th>
                    {isAdmin && <th>User</th>}
                    <th>{t('order.tableHeaders.code')}</th>
                    <th style={{ textAlign: 'center' }}>{t('order.tableHeaders.productId')}</th>
                    <th style={{ textAlign: 'center' }}>{t('order.tableHeaders.mailType')}</th>
                    <th style={{ textAlign: 'center' }}>SL</th>
                    <th style={{ textAlign: 'center' }}>{t('order.tableHeaders.total')}</th>
                    <th style={{ textAlign: 'center' }}>{t('order.tableHeaders.status')}</th>
                    <th>{t('order.tableHeaders.createdDate')}</th>
                    <th style={{ textAlign: 'right', width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {orderList.map((order, i) => (
                    <tr key={`entity-${i}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          style={{ cursor: 'pointer', accentColor: colors.accent }}
                        />
                      </td>
                      {isAdmin && <td style={{ fontWeight: 500 }}>{order.user ? order.user.login : ''}</td>}
                      <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: colors.accent, fontWeight: 600 }}>
                        {order.code}
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: colors.dim }}>
                        {order.mailType?.id || '\u2014'}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: 13 }}>{order.mailType?.name}</td>
                      <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{order.quantity}</td>
                      <td
                        style={{
                          textAlign: 'center',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontWeight: 600,
                          color: colors.success,
                          fontSize: 13,
                        }}
                      >
                        {formatVND(order.totalPrice)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {order.isApproved ? (
                          <span className="ms-badge ms-badge--success">{t('order.success')}</span>
                        ) : (
                          <span className="ms-badge ms-badge--danger">{t('order.failed')}</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: colors.dim }}>
                        {order.createdDate && <TextFormat type="date" value={order.createdDate} format={APP_TIMESTAMP_FORMAT} />}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {order.result && (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                            <button
                              className="ms-btn ms-btn--secondary"
                              onClick={() => setViewOrder(order)}
                              style={{ padding: '4px 10px', fontSize: 11 }}
                            >
                              <FontAwesomeIcon icon="eye" style={{ marginRight: 4 }} />
                              {t('order.view')}
                            </button>
                            <button
                              className="ms-btn ms-btn--ghost"
                              onClick={() => onDownload(order.result, order.code)}
                              style={{ padding: '4px 10px', fontSize: 11 }}
                            >
                              <FontAwesomeIcon icon="download" style={{ marginRight: 4 }} />
                              {t('order.downloadShort')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItems ? (
              <div
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <PageSizeSelector currentSize={pageSize} onSizeChange={handleSizeChange} />
                <div style={{ color: '#94a3b8', fontSize: 13 }}>
                  <JhiItemCount page={pagination.activePage} total={totalItems} itemsPerPage={pageSize} />
                </div>
                <JhiPagination
                  activePage={pagination.activePage}
                  onSelect={handlePagination}
                  maxButtons={5}
                  itemsPerPage={pageSize}
                  totalItems={totalItems}
                />
              </div>
            ) : null}
          </>
        ) : (
          !loading && (
            <div style={{ padding: 50, textAlign: 'center' }}>
              <div style={{ color: colors.dim, fontSize: 15 }}>{hasAnyFilter ? t('order.noResults') : t('order.noOrders')}</div>
            </div>
          )
        )}
      </div>

      {/* View order detail modal */}
      <Modal
        isOpen={!!viewOrder}
        toggle={() => setViewOrder(null)}
        centered
        modalTransition={{ timeout: 20 }}
        backdropTransition={{ timeout: 10 }}
      >
        {viewOrder && (
          <>
            <ModalHeader toggle={() => setViewOrder(null)}>{t('order.orderDetail')}</ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: colors.dim, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    {t('order.orderCodeLabel')}
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: colors.accent, fontSize: 15 }}>
                    {viewOrder.code}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: colors.muted }}>
                    {t('order.typeLabel')}: <strong style={{ color: colors.text }}>{viewOrder.mailType?.name}</strong> (ID:{' '}
                    {viewOrder.mailType?.id})
                  </div>
                  <div style={{ fontSize: 12, color: colors.muted }}>
                    {t('order.quantityLabel')}: <strong style={{ color: colors.text }}>{viewOrder.quantity}</strong> —{' '}
                    {t('order.totalLabel')}: <strong style={{ color: colors.success }}>{formatVND(viewOrder.totalPrice)}</strong>
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: 14,
                  borderRadius: 10,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  maxHeight: 280,
                  overflowY: 'auto',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  lineHeight: 1.8,
                  color: colors.muted,
                }}
              >
                {JSON.parse(viewOrder.result).map((x, idx) => (
                  <div key={idx}>{convertMailPass(x)}</div>
                ))}
              </div>
            </ModalBody>
            <ModalFooter style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="ms-btn ms-btn--secondary"
                onClick={() => onCopy(viewOrder.result)}
                style={{ padding: '8px 16px', fontSize: 12 }}
              >
                {t('order.copyEmail')}
              </button>
              <button
                className="ms-btn ms-btn--secondary"
                onClick={() => onCopyFullInfo(viewOrder.result)}
                style={{ padding: '8px 16px', fontSize: 12 }}
              >
                {t('order.copyFull')}
              </button>
              <button
                className="ms-btn ms-btn--primary"
                onClick={() => onDownload(viewOrder.result, viewOrder.code)}
                style={{ padding: '8px 16px', fontSize: 12 }}
              >
                {t('order.download')}
              </button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Order;
